import { InputText } from "primereact/inputtext";
import extractDataFromMap from "../../utils/ExtractDataFromMap";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { format as dateFnsFormat, parse } from "date-fns";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import assignDataToMap from "../../utils/AssignDataToMap";

const genElText = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <InputText
        id={field}
        disabled={disabled}
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.target.value, map: newData });
          setData(newData);
        }}
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElNumber = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  min,
  max,
  showButtons,
  emptyWhenZero,
  placeholder,
  error = {},
}) => {
  // Integer-only plain text input instead of PrimeReact's InputNumber: InputNumber handles Android soft keyboards
  // only through the deprecated keypress event (and preventDefaults everything else), so on many phones the
  // digits never reach the field. This reads the real input event instead.
  // emptyWhenZero: 0 is shown as an empty input with "0" as the placeholder, and a cleared input is stored as 0
  const current = data[field] ?? 0;
  const upper = max ?? 999999999999;
  const lower = min ?? 0;

  const commit = (n) => {
    const newValue = emptyWhenZero ? n ?? 0 : n;
    if (newValue === current) return;
    const newData = { ...data };
    assignDataToMap({ key: field, value: newValue, map: newData });
    setData(newData);
  };

  const onTyped = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) return commit(null);
    const n = Math.min(parseInt(digits, 10), upper);
    // below min counts as empty (typing the "0" of "05"), instead of jumping to min mid-typing
    commit(n < lower ? 0 : n);
  };

  const step = (delta) => {
    commit(Math.min(Math.max(current + delta, lower), upper));
  };

  return (
    <div className={`col-start-start gap-1 min-w-0 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <div className="p-inputgroup w-full min-w-0">
        {showButtons && (
          <Button
            type="button"
            icon="pi pi-angle-down"
            disabled={disabled}
            onClick={() => step(-1)}
          />
        )}
        <InputText
          id={field}
          className={`w-full min-w-0 !w-[1%] ${
            error[field] ? "bg-red-100" : ""
          }`}
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder ?? (emptyWhenZero ? "0" : undefined)}
          value={current ? current.toLocaleString("id-ID") : ""}
          onChange={onTyped}
        />
        {showButtons && (
          <Button
            type="button"
            icon="pi pi-angle-up"
            disabled={disabled}
            onClick={() => step(1)}
          />
        )}
      </div>
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElTime = ({
  field,
  name,
  data,
  setData,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MobileTimePicker
          disabled={disabled}
          className={`w-full ${error[field] ? "bg-red-100" : ""}`}
          value={value}
          ampm={false}
          onChange={(e) => {
            const newData = { ...data };
            assignDataToMap({ key: field, value: e, map: newData });
            setData(newData);
          }}
        />
      </LocalizationProvider>
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElDate = ({
  field,
  name,
  data,
  setData,
  primeFormat,
  disabled,
  width,
  showTime,
  autoFocus,
  error = {},
}) => {
  let value = data && data[field] ? data[field] : null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <Calendar
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        id={field}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value ?? null}
        showIcon
        showButtonBar
        showTime={showTime}
        dateFormat={primeFormat ?? "dd/mm/yy"}
        hourFormat="24"
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.value, map: newData });
          setData(newData);
        }}
        locale="id"
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElDateRange = ({
  field,
  name,
  data,
  setData,
  primeFormat,
  disabled,
  width,
  autoFocus,
  error = {},
}) => {
  let value = data[field] ?? null;

  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <Calendar
        className={`w-full ${error[field] ? "bg-red-100" : ""}`}
        id={field}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value ?? null}
        selectionMode="range"
        hideOnRangeSelection
        showIcon
        showButtonBar
        dateFormat={primeFormat ?? "dd/mm/yy"}
        hourFormat="24"
        onChange={(e) => {
          const newData = { ...data };
          assignDataToMap({ key: field, value: e.value, map: newData });
          setData(newData);
        }}
        locale="id"
      />
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

const genElObject = ({
  name,
  field,
  value,
  onClick,
  onClear,
  disabled,
  autoFocus,
  width,
  error = {},
}) => {
  return (
    <div className={`col-start-start gap-1 ${width ?? "w-full"}`}>
      <label htmlFor={field} className="font-bold text-sm text-slate-500">
        {name}
      </label>
      <div className="row-start-center gap-1 w-full">
        <Button
          disabled={disabled}
          className={`bg-indigo-50 border-indigo-100 w-full text-slate-700 leading-tight ${
            error[field] ? "bg-red-100" : ""
          }`}
          outlined
          onClick={(e) => {
            e.preventDefault();
            if (onClick) {
              onClick();
            }
          }}
        >
          <div className="row-between-center w-100">
            {!value || value.length < 0 ? "-" : value}
            <i className="pi pi-search" />
          </div>
        </Button>
        {onClear && (
          <Button
            className="bg-red-400 border-red-400 max-w-[1.8rem] max-h-[1.8rem]"
            icon="pi pi-times"
            rounded
            severity="danger"
            onClick={(e) => {
              e.preventDefault();
              if (onClear) {
                onClear();
              }
            }}
          />
        )}
      </div>
      {error[field] && (
        <small className="font-semibold text-red-400">{error[field]}</small>
      )}
    </div>
  );
};

export {
  genElDateRange,
  genElObject,
  genElDate,
  genElText,
  genElNumber,
  genElTime,
};
